// frontend/src/i18n/I18nContext.tsx
/**
 * Internationalization (i18n) Context Provider
 * 
 * Provides language state and translation functions to the entire app.
 * Persists language preference to localStorage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import logger from "utils/logger";
import {
  SupportedLanguage,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  TranslationDictionary,
  TranslatedStrings,
  LanguageInfo,
} from "./types";
import { en } from "./translations/en";

// ============================================================================
// LAZY-LOADED TRANSLATIONS
// ============================================================================

/**
 * Map of language codes to their translation modules.
 * Uses dynamic imports for code splitting - only loads needed languages.
 */
/**
 * Map of language codes to their translation modules.
 * Uses dynamic imports for code splitting - only loads needed languages.
 */
const translationLoaders: Record<SupportedLanguage, () => Promise<{ default: TranslationDictionary | TranslatedStrings }>> = {
  en: () => Promise.resolve({ default: en }),
  zh: () => import("./translations/zh"),
  es: () => import("./translations/es"),
  hi: () => import("./translations/hi"),
  ar: () => import("./translations/ar"),
  pt: () => import("./translations/pt"),
  fr: () => import("./translations/fr"),
  de: () => import("./translations/de"),
  ja: () => import("./translations/ja"),
  ko: () => import("./translations/ko"),
  it: () => import("./translations/it"),
  ru: () => import("./translations/ru"),
  nl: () => import("./translations/nl"),
  tr: () => import("./translations/tr"),
  pl: () => import("./translations/pl"),
  ca: () => import("./translations/ca"),
  eu: () => import("./translations/eu"),
};

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface I18nContextValue {
  /** Current active language */
  language: SupportedLanguage;
  
  /** Change the active language */
  setLanguage: (lang: SupportedLanguage) => void;
  
  /** 
   * Get translated string by key
   * @param key - Translation key (e.g., "common.save")
   * @param defaultValueOrPlaceholders - Either a default string (for backward compatibility with react-i18next)
   *                                     or an object of placeholders to substitute
   */
  t: (key: string, defaultValueOrPlaceholders?: string | Record<string, string | number>) => string;
  
  /** List of all supported languages */
  languages: LanguageInfo[];
  
  /** Whether translations are still loading */
  isLoading: boolean;
  
  /** Current text direction (ltr/rtl) */
  direction: "ltr" | "rtl";
  
  /** Get the current language info */
  currentLanguage: LanguageInfo;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

// ============================================================================
// STORAGE KEYS
// ============================================================================

const STORAGE_KEY = "athena_language";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract text from translation entry (handles both full entries and simple strings)
 */
function getTextFromEntry(entry: unknown): string {
  if (typeof entry === "string") {
    return entry;
  }
  if (entry && typeof entry === "object" && "text" in entry) {
    return (entry as { text: string }).text;
  }
  return "";
}

/**
 * Substitute placeholders in a string.
 * E.g., "Hello {name}" with {name: "John"} => "Hello John"
 */
function substitutePlaceholders(
  text: string,
  placeholders?: Record<string, string | number>
): string {
  if (!placeholders) return text;
  
  return Object.entries(placeholders).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value));
  }, text);
}

/**
 * Get initial language from storage or browser settings
 */
function getInitialLanguage(): SupportedLanguage {
  // Check localStorage
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
      return stored as SupportedLanguage;
    }
    
    // Check browser language
    const browserLang = navigator.language.split("-")[0];
    if (SUPPORTED_LANGUAGES.some(l => l.code === browserLang)) {
      return browserLang as SupportedLanguage;
    }
  }
  
  return DEFAULT_LANGUAGE;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface I18nProviderProps {
  children: ReactNode;
  /** Override initial language (useful for testing) */
  initialLanguage?: SupportedLanguage;
}

export function I18nProvider({ children, initialLanguage }: I18nProviderProps) {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    initialLanguage || getInitialLanguage()
  );
  const [translations, setTranslations] = useState<TranslationDictionary | TranslatedStrings>(en);
  const [isLoading, setIsLoading] = useState(false);

  // Load translations when language changes
  useEffect(() => {
    const loadTranslations = async () => {
      if (language === "en") {
        setTranslations(en);
        return;
      }

      setIsLoading(true);
      try {
        const loader = translationLoaders[language];
        if (loader) {
          const module = await loader();
          // Merge with English as fallback - cast to common type
          const merged = { ...en, ...module.default } as TranslationDictionary;
          setTranslations(merged);
        }
      } catch (error) {
        logger.error(`Failed to load translations for ${language}:`, error);
        // Fallback to English
        setTranslations(en);
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  // Persist language preference
  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, lang);
    }
  }, []);

  // Translation function - supports both react-i18next patterns:
  // t("key", "default value") - backward compatibility
  // t("key", { name: "value" }) - placeholder substitution
  const t = useCallback(
    (key: string, defaultValueOrPlaceholders?: string | Record<string, string | number>): string => {
      // Determine if second arg is a default value (string) or placeholders (object)
      const isDefaultValue = typeof defaultValueOrPlaceholders === "string";
      const defaultValue = isDefaultValue ? defaultValueOrPlaceholders : undefined;
      const placeholders = isDefaultValue ? undefined : defaultValueOrPlaceholders;
      
      const entry = translations[key];
      
      if (!entry) {
        // Fallback: check English
        const englishEntry = en[key];
        if (englishEntry) {
          const text = getTextFromEntry(englishEntry);
          return substitutePlaceholders(text, placeholders);
        }
        // Key not found - return default value if provided, otherwise return key
        if (defaultValue) {
          return defaultValue;
        }
        logger.warn(`[i18n] Missing translation: ${key}`);
        return key;
      }

      const text = getTextFromEntry(entry);
      return substitutePlaceholders(text, placeholders);
    },
    [translations]
  );

  // Get current language info
  const currentLanguage = useMemo(
    () => SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0],
    [language]
  );

  // Memoize context value
  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t,
      languages: SUPPORTED_LANGUAGES,
      isLoading,
      direction: currentLanguage.direction,
      currentLanguage,
    }),
    [language, setLanguage, t, isLoading, currentLanguage]
  );

  // Apply RTL direction to document
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dir = currentLanguage.direction;
      document.documentElement.lang = language;
    }
  }, [currentLanguage.direction, language]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access i18n context
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

/**
 * Shorthand hook for translation function only
 */
export function useTranslation() {
  const { t, language, direction } = useI18n();
  return { t, language, direction };
}

/**
 * Hook for language switching UI
 */
export function useLanguageSwitcher() {
  const { language, setLanguage, languages, currentLanguage } = useI18n();
  return { language, setLanguage, languages, currentLanguage };
}

export default I18nContext;
